import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        portfolio: 'Gallery',
        inquiry: 'Shop Now',
        contact: 'Contact',
        about: 'About',
        reviews: 'Reviews',
        order: 'Order',
        terms: 'Terms',
        privacy: 'Privacy',
        pickup: 'Pickup',
        forBusiness: 'Business',
        charity: 'Charity'
      },

      // Terms Page
      terms: {
        title: "Terms & Privacy",
        ordering: {
          title: "Ordering Process",
          p1: "All cakes are made to order based on custom requests submitted through our online platforms.",
          p2: "We do not maintain a physical walk-in store with a ready-made cake showcase. All consultations and orders are handled online."
        },
        delivery: {
          title: "Pickup & Delivery",
          p1: "All orders are for self-pickup only at our designated location in Seminyak, Bali.",
          p2: "We do not provide an in-house delivery service and do not have an internal delivery team. Customers are responsible for arranging their own pickup, whether in person or via a third-party courier service (e.g., Gojek, Grab).",
          p3: "We are not responsible for the condition of the cake once it has left our premises. We ensure every cake is in perfect condition upon pickup."
        },
        privacy: {
          title: "Privacy Policy",
          p1: "We collect personal information such as your name, email, and phone number solely for the purpose of processing your order and communication.",
          p2: "We are committed to protecting your privacy and will not share your personal data with third parties without your consent, except as required by law."
        }
      },

      // Hero Section
      hero: {
        title: 'Amazing Variety\nOf Cakes Starting\nJust $6',
        subtitle: 'Experience the finest quality cakes with ingredients and affordable prices. Made with love and care.',
        cta1: 'SHOP',
        cta2: 'View Gallery',
        badge: 'Best Quality',
        features: {
          collection: 'Cake Collection',
          quality: 'Premium Quality',
          guarantee: '100% Money Back'
        }
      },

      // Hero Slider
      slider: {
        slides: [
          {
            badge: 'Online Service',
            title: 'Online Premium\nOrders',
            subtitle: 'We accept premium custom cake orders online only'
          },
          {
            badge: 'Signature Collection',
            title: 'Premium Cakes',
            subtitle: 'Experience the luxury of quality cakes with unique designs crafted with love and high dedication.'
          },
          {
            badge: 'Birthday Special',
            title: 'Birthday Cakes\nThat Wow',
            subtitle: 'Make your celebration special with spectacular birthday cakes that will amaze all your guests.'
          },
          {
            badge: 'Wedding Dreams',
            title: 'Dream Wedding\nCakes',
            subtitle: 'Create magical moments with stunning wedding cakes designed to match your dream celebration.'
          },
          {
            badge: 'Sweet Cupcakes',
            title: 'Perfect\nCupcakes',
            subtitle: 'Bring happiness in small bites with quality cupcakes perfect for every occasion.'
          },
          {
            badge: 'For Business',
            title: 'For Business\nPartnerships',
            subtitle: 'Tailored cakes and desserts designed to impress your guests and elevate your corporate image. Perfect for hotels, restaurants, cafés, and events.',
            ctaLabel: 'For Business',
            ctaLink: '/for-business',
            hidePrimaryCta: true
          },
          {
            badge: 'Halal Certified',
            title: 'Halal Certified',
            subtitle: 'We are committed to providing halal-certified cakes with the highest quality ingredients and standards.',
            ctaLabel: 'Learn More',
            ctaLink: '/about',
            hidePrimaryCta: true
          },
        ]
      },

      // About Section
      about: {
        title: 'Why Choose TN Cakes Bali®?',
        subtitle: 'We blend artistry with taste to create cakes that are as beautiful as they are delicious',
        custom: {
          title: 'Custom Design',
          description: 'We bring your unique vision to life with personalized designs for your celebration'
        },
        quality: {
          title: 'Quality Assurance',
          description: 'Only the finest ingredients used to ensure exceptional taste and beautiful presentation'
        },
        guarantee: {
          title: 'Satisfaction Guaranteed',
          description: 'We guarantee your satisfaction with every custom creation we make'
        },
        timely: {
          title: 'Timely Pickup',
          description: 'Your cake will be ready exactly when you need it, fresh and beautifully presented'
        },
        care: {
          title: 'Personal Attention',
          description: 'Every order receives our personal attention to ensure your complete satisfaction'
        },
        story: {
          title: 'Our Story',
          paragraph1: 'TN Cakes Bali® was born from a passion for creating beautiful, memorable moments through exceptional cake artistry. Based in the heart of Seminyak, Bali, we have been serving the local and international community with premium custom cakes since 2019.',
          paragraph2: 'Our founder, Tina, began her journey in professional baking after years of creating cakes for family and friends. What started as a hobby quickly grew into a thriving business known for its attention to detail, quality ingredients, and stunning designs.',
          paragraph3: 'Today, TN Cakes Bali® is proud to be one of Bali\'s most trusted names in custom cake design, having created over 1000 cakes for birthdays, weddings, corporate events, and celebrations of all kinds.'
        },
        mission: {
          title: 'Our Mission',
          description: 'To provide unforgettable custom cake experiences by combining artistic creativity, premium ingredients, and exceptional customer service for every special celebration.'
        },
        vision: {
          title: 'Our Vision',
          description: 'To be Bali\'s leading custom cake provider, renowned for innovation, quality, and the ability to transform dreams into beautiful culinary creations.'
        },
        stats: {
          experience: 'Experiences years',
          customers: 'Happy customers',
          cakes: 'Cakes created',
          rating: 'Average Rating'
        }
      },

      // Features Section  
      features: {
        title: 'What Makes Us Special',
        subtitle: 'Discover why TN Cakes Bali® is the perfect choice for your celebration',
        handmade: {
          title: 'Artisan Craftsmanship',
          description: 'Each cake is handcrafted by skilled artisans with years of experience and passion for perfection'
        },
        custom: {
          title: 'Custom Design',
          description: 'We bring your unique vision to life with personalized designs for your celebration'
        },
        premium: {
          title: 'Quality Assurance',
          description: 'Only the finest ingredients used to ensure exceptional taste and beautiful presentation'
        },
        guarantee: {
          title: 'Satisfaction Guaranteed',
          description: 'We guarantee your satisfaction with every custom creation we make'
        },
        timely: {
          title: 'Timely Pickup',
          description: 'Your cake will be ready exactly when you need it, fresh and beautifully presented'
        },
        care: {
          title: 'Personal Attention',
          description: 'Every order receives our personal attention to ensure your complete satisfaction'
        },
        rating: '5.0/5 Stars',
        reviews: 'Based on 567+ Reviews'
      },

      // Product Categories
      categories: {
        title: 'Our Specialties',
        subtitle: 'Custom cakes designed for every special moment in your life',
        birthday: {
          title: 'Birthday Cakes',
          description: 'Magical birthday cakes that make every celebration unforgettable with personal design and taste'
        },
        wedding: {
          title: 'Wedding Cakes',
          description: 'Elegant wedding masterpieces with stunning details, ingredients, and romantic designs'
        },
        cupcakes: {
          title: 'Cupcakes',
          description: 'Artisan cupcakes with gourmet flavors, beautiful decorations, and perfect individual portions'
        },
        babyshower: {
          title: 'Baby Shower Cakes',
          description: 'Adorable baby-themed cakes with soft colors, charming designs, and pregnancy-safe ingredients'
        },
        celebration: {
          title: 'Celebration Cakes',
          description: 'Custom celebration cakes for graduations, anniversaries, and all your special moments'
        },
        corporate: {
          title: 'Corporate Cakes',
          description: 'Professional corporate cakes with custom branding, elegant presentation for business events'
        },
        cta: {
          title: 'Ready to Order Your Dream Cake?',
          description: 'Let us help you create the perfect centerpiece for your celebration',
          portfolio: 'View Gallery',
          inquiry: 'Shop Now'
        }
      },

      // Common Elements
      common: {
        readMore: 'Read More',
        readLess: 'Read Less',
        loading: 'Loading...',
        error: 'An error occurred',
        success: 'Success',
        submit: 'Submit',
        cancel: 'Cancel',
        close: 'Close',
        save: 'Save',
        edit: 'Edit',
        delete: 'Delete',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        viewAll: 'View All',
        learnMore: 'Learn More',
        note: 'Note'
      },

      // Forms
      forms: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        message: 'Message',
        subject: 'Subject',
        required: 'This field is required',
        invalidEmail: 'Please enter a valid email address',
        invalidPhone: 'Please enter a valid phone number',
        sending: 'Sending...',
        sent: 'Message sent successfully!',
        error: 'Failed to send message. Please try again.'
      },

      // Shop Page
      inquiry: {
        title: 'Tell Us About Your Dream Cake',
        subtitle: 'We\'d love to hear your ideas! Fill the form below and let\'s create something beautiful together.',
        formTitle: 'Order Form',
        formSubtitle: 'Tell us about your dream cake details',

        personalInfo: 'Personal Information',
        cakeDetails: 'Cake Details',

        fullName: 'Full Name',
        email: 'Email Address',
        whatsapp: 'WhatsApp Number',
        dateNeeded: 'Date Needed',
        occasion: 'Occasion',
        servings: 'Number of peoples catering for',
        theme: 'Theme/Style Description',
        message: 'Additional Message',
        specialRequests: 'Special Requests',
        uploadImages: 'Upload a screenshot of your cake inspiration. For design ideas please visit our instagram @tncakebali.',
        optional: 'Optional',

        placeholders: {
          fullName: 'e.g. Jane Doe',
          email: 'e.g. jane.doe@example.com',
          whatsapp: 'e.g. 812 3456 7890',
          occasion: 'Select an occasion...',
          theme: 'Describe your preferred design, colors, size, and any additional requirements...',
          message: 'Any special dietary requirements, pick up instructions, or other requests...',
        },

        uploadText: 'Drop files here or click to browse',
        uploadHint: 'You can upload multiple images (PNG, JPG up to 5MB each)',
        filesSelected: 'files selected',

        submitButton: 'Shop Now',
        submitting: 'Sending, please wait...',
        submitHint: 'We will respond within 24 hours with a detailed quote.',
        submittingHint: 'Processing your order, please don\'t close this page.',
        responseTime: 'We will get back to you within 24 hours.',

        successTitle: 'Order Sent!',
        successMessage: 'Thank you for your order! We\'ll get back to you within 24 hours with a detailed quote and design proposal.',
        errorTitle: 'Submission Failed',
        errorMessage: 'Sorry, there was an error sending your order. Please try again or contact us directly via WhatsApp.',

        occasions: {
          birthday: 'Birthday Party',
          wedding: 'Wedding',
          anniversary: 'Anniversary',
          babyShower: 'Baby Shower',
          corporate: 'Corporate Event',
          graduation: 'Graduation',
          other: 'Other Celebration'
        },

        howItWorks: {
          title: 'How It Works',
          subtitle: 'Easy and transparent process to get your dream custom cake',
          step1: {
            title: 'Shop Now',
            description: 'Fill out the form with your cake requirements and inspiration'
          },
          step2: {
            title: 'Get Quote',
            description: 'Receive detailed quote and design proposal promptly'
          },
          step3: {
            title: 'Confirm Order',
            description: 'Approve the design and make deposit to secure your date'
          },
          step4: {
            title: 'Enjoy Your Cake',
            description: 'Your beautiful custom cake will be ready for pickup on time'
          }
        },

        stats: {
          cakes: 'Cakes created',
          response: 'Fast Response',
          rating: 'Customer Rating'
        },

        securityChallenge: 'Please add the 2 numbers and place the total in the box below',
        answerPlaceholder: 'Answer',
        securityProtected: 'Protected by anti-bot security'
      },

      // Contact Page
      contact: {
        title: 'Get In Touch',
        subtitle: 'Ready to discuss your dream cake? We\'re here to help make your celebration unforgettable.',
        whatsappTony: 'WhatsApp Tony (English)',
        whatsappNila: 'WhatsApp Nila (Bahasa)',
        whatsappButton: 'Chat on WhatsApp',
        location: 'Visit Our Store',
        address: 'Jl. Dewi Saraswati No.9\nSeminyak, Bali\nIndonesia',
        getDirections: 'Get Directions',
        findUs: 'Find Us',
        businessHours: 'Business Hours',
        followUs: 'Follow Us',
        bookingNote: 'We recommend booking consultations in advance to ensure availability.',
        hours: {
          monday: 'Monday - Friday',
          mondayTime: '9:00 AM - 6:00 PM',
          saturday: 'Saturday',
          saturdayTime: '9:00 AM - 4:00 PM',
          sunday: 'Sunday',
          sundayTime: 'By Appointment Only'
        },
        defaultMessages: {
          tony: 'Hi Tony! I\'m interested in ordering a custom cake. Could you please provide more information?',
          nila: 'Hi Nila! I\'m interested in ordering a custom cake. Could you please provide more information?'
        },
        faq: {
          title: 'Frequently Asked Questions',
          questions: {
            advance: {
              question: 'How far in advance should I order?',
              answer: 'We recommend ordering at least 2-3 weeks in advance for custom cakes, especially during peak seasons.'
            },
            pickup: {
              question: 'Do you offer pickup service?',
              answer: 'Yes, all cakes are available for pickup at our location in Bali. We do not offer delivery service.'
            },
            dietary: {
              question: 'Can you accommodate dietary restrictions?',
              answer: 'Absolutely! We can create gluten-free, vegan, and sugar-free options. Please mention this in your order.'
            },
            cancellation: {
              question: 'What is your cancellation policy?',
              answer: 'Cancellations made 72 hours before pickup are eligible for a 50% refund of the deposit.'
            }
          }
        },
        cta: {
          title: 'Ready to Start Your Order?',
          subtitle: 'Don\'t wait – let\'s create your perfect cake today!',
          sendInquiry: 'Shop Now',
          whatsappUs: 'WhatsApp Us'
        }
      },

      // Portfolio Page
      portfolio: {
        title: 'Our Gallery',
        subtitle: 'Discover our collection of custom-crafted cakes that have made celebrations unforgettable. Each creation tells a story of love, artistry, and exceptional taste.',
        followInstagram: 'Follow @tncakesbali',
        categories: {
          all: 'All Cakes',
          wedding: 'Wedding Cakes',
          birthday: 'Birthday Cakes',
          cupcakes: 'Cupcakes',
          corporate: 'Corporate Events',
          baby: 'Baby Shower',
          celebration: 'Celebration Cakes'
        },
        viewDetails: 'View Details',
        learnMore: 'Learn More',
        modal: {
          aboutCreation: 'About This Creation',
          keyFeatures: 'Key Features',
          perfectFor: 'Perfect For',
          preparationTime: 'Preparation Time',
          interestedTitle: 'Interested in This Design?',
          startOrder: 'Start Your Order',
          whatsappUs: 'WhatsApp Us'
        },
        cta: {
          title: 'Ready to Create Your Dream Cake?',
          subtitle: 'Let\'s discuss your vision and bring it to life with our quality custom cake service.',
          startOrder: 'Start Your Order',
          whatsappUs: 'WhatsApp Us'
        }
      },

      // Footer
      footer: {
        description: 'Creating beautiful, delicious custom cakes for your special moments in Bali.',
        quickLinks: 'Quick Links',
        services: 'Services',
        contact: 'Contact Information',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        hours: 'Opening Hours',
        social: 'Follow Us',
        copyright: '© 2025 TN Cakes Bali®. All rights reserved.',
        newsletter: {
          title: 'Stay Updated',
          subtitle: 'Subscribe to receive updates about new cake designs and special offers',
          placeholder: 'Enter your email',
          subscribe: 'Subscribe',
          success: 'Thank you for subscribing!',
          error: 'Please enter a valid email address'
        }
      },

      // Global CTA Section
      globalCTA: {
        title: 'Ready to Make Your Moment Special?',
        subtitle: 'Join hundreds of satisfied customers who have trusted TN Cakes Bali® for their special moments.',
        orderNow: 'Order Now',
        viewGallery: 'View Gallery',
        contactUs: 'Contact Us'
      },

      // Home Page CTA Section
      homeCTA: {
        title: 'Ready to Create Your Dream Cake?',
        subtitle: 'Let\'s bring your vision to life with our premium custom cake service. Contact us today to start planning your perfect celebration.',
        shopNow: 'Shop Now',
        viewPortfolio: 'View Portfolio'
      },

      // For Business Page
      forBusiness: {
        hero: {
          title: 'For Business Partnerships',
          subtitle: 'Tailored cakes and desserts designed to impress your guests and elevate your corporate image. Perfect for hotels, restaurants, cafés, and events.'
        },
        value: {
          quality: {
            title: 'Premium Quality',
            description: 'Premium, halal ingredients, fresh every day.'
          },
          branding: {
            title: 'Custom Branding',
            description: 'Request cakes with your company logo or special event theme.'
          },
          delivery: {
            title: 'Reliable Delivery',
            description: 'On-time delivery for your events and guest needs.'
          },
          orders: {
            title: 'Flexible Orders',
            description: 'Bulk & repeat order systems available.'
          }
        },
        solutions: {
          title: 'Packages & Solutions',
          hotels: {
            title: 'Hotel & Resort',
            description: 'Welcome cakes, honeymoon packages, room surprises.'
          },
          corporate: {
            title: 'Corporate Events',
            description: 'Meetings, seminars, workshops, launching events.'
          },
          cafe: {
            title: 'Café & Restaurant',
            description: 'Dessert collaborations, seasonal menus.'
          },
          retail: {
            title: 'Retail & Wholesale',
            description: 'Bulk cupcakes, hampers for resellers.'
          }
        },
        partners: {
          title: 'Who is trust in us',
          testimonial: 'Working with TN Cakes has elevated our guest experience. Their quality and service are unmatched.',
          testimonialFrom: 'Manager, The Grand Hotel'
        },
        cta: {
          title: 'Let\'s Collaborate',
          button: 'Send Inquiry',
          altCta: 'Or contact us directly via WhatsApp Business.'
        },
        form: {
          businessName: 'Business Name',
          businessType: 'Business Type',
          needs: 'Describe Your Needs',
          orderEstimate: 'Estimated Order Quantity',
          contact: 'Contact Person (Name & Phone)'
        }
      },

      // Charity Page
      charity: {
        title: "Sharing Sweetness, Spreading Kindness",
        subtitle: "Every slice of cake is not just for celebrating, but also for sharing happiness with those in need.",
        about: {
          title: "About Our Charity Program",
          description: "We believe that business is not just about profit, but also about making a positive impact on society. A portion of our profits and specific orders are allocated to social programs focused on children, orphanages, and local communities in Bali."
        },
        support: {
          title: "How You Can Support",
          cakeDonation: {
            title: "Cake Donations",
            description: "We deliver cakes to orphanages, schools, and community events to share moments of joy."
          },
          events: {
            title: "Charity Events",
            description: "We collaborate with foundations and NGOs to organize social events and fundraisers."
          },
          participation: {
            title: "Customer Participation",
            description: "Every purchase of a custom cake contributes IDR 50,000 to our charity fund."
          }
        },
        gallery: {
          title: "Stories of Kindness",
          subtitle: "A glimpse into the moments of joy we've shared through our charity activities."
        },
        cta: {
          title: "Join Us to Spread Kindness",
          subtitle: "Collaborate with us to bring more smiles to more faces. Whether you want to donate funds, goods, or partner for an event, we're open to all forms of support.",
          button: "Collaborate With Us"
        },
        form: {
          title: "Collaboration & Donation Form",
          name: "Full Name or Organization",
          contact: "Contact (Phone/Email)",
          contributionType: "Type of Contribution",
          message: "Your Message",
          submit: "Send Inquiry",
          contributionOptions: {
            donation: "Financial Donation",
            goods: "Goods Donation",
            event: "Event Collaboration"
          }
        },
        stats: {
          cakesDonated: "Cakes Donated",
          eventsSupported: "Charity Events Supported",
          smilesDelivered: "Smiles Delivered"
        }
      },
    }
  },
  id: {
    translation: {
      // Navigation
      nav: {
        home: 'Beranda',
        portfolio: 'Galeri',
        inquiry: 'Belanja Sekarang',
        contact: 'Kontak',
        about: 'About',
        reviews: 'Ulasan',
        order: 'Pesan',
        terms: 'Syarat',
        privacy: 'Privasi',
        pickup: 'Ambil',
        forBusiness: 'Bisnis',
        charity: 'Amal'
      },

      // Terms Page
      terms: {
        title: "Syarat & Privasi",
        ordering: {
          title: "Proses Pemesanan",
          p1: "Semua kue dibuat berdasarkan pesanan custom yang dikirimkan melalui platform online kami.",
          p2: "Kami tidak memiliki toko fisik walk-in dengan showcase kue yang sudah jadi. Semua konsultasi dan pemesanan dilakukan secara online."
        },
        delivery: {
          title: "Pengambilan & Pengiriman",
          p1: "Semua pesanan hanya untuk diambil sendiri (self-pickup) di lokasi kami yang telah ditentukan di Seminyak, Bali.",
          p2: "Kami tidak menyediakan layanan pengiriman internal dan tidak memiliki tim pengiriman sendiri. Pelanggan bertanggung jawab untuk mengatur pengambilan pesanan mereka, baik secara langsung maupun melalui jasa kurir pihak ketiga (misalnya Gojek, Grab).",
          p3: "Kami tidak bertanggung jawab atas kondisi kue setelah keluar dari lokasi kami. Kami memastikan setiap kue dalam kondisi sempurna saat diambil."
        },
        privacy: {
          title: "Kebijakan Privasi",
          p1: "Kami mengumpulkan informasi pribadi seperti nama, email, dan nomor telepon Anda semata-mata untuk tujuan memproses pesanan dan komunikasi.",
          p2: "Kami berkomitmen untuk melindungi privasi Anda dan tidak akan membagikan data pribadi Anda kepada pihak ketiga tanpa persetujuan Anda, kecuali diwajibkan oleh hukum."
        }
      },

      // Hero Section
      hero: {
        title: 'Beragam Kue\nMenarik Mulai dari\nHanya $6',
        subtitle: 'Nikmati kue berkualitas terbaik dengan bahan dan harga terjangkau. Dibuat dengan cinta dan perhatian.',
        cta1: 'Beli',
        cta2: 'Lihat Galeri',
        badge: 'Kualitas Terbaik',
        features: {
          collection: 'Koleksi Kue',
          quality: 'Kualitas Premium',
          guarantee: '100% Garansi Uang Kembali'
        }
      },

      // Hero Slider
      slider: {
        slides: [
          {
            badge: 'Layanan Online',
            title: 'Online Premium\nCake Orders',
            subtitle: 'Kami hanya menerima pesanan kue custom premium secara online — tidak ada toko walk‑in.'
          },
          {
            badge: 'Koleksi Signature',
            title: 'Kue Premium',
            subtitle: 'Nikmati kelezatan kue berkualitas dengan design unik yang dibuat khusus dengan cinta dan dedikasi tinggi.'
          },
          {
            badge: 'Spesial Ulang Tahun',
            title: 'Kue Ulang Tahun\nyang Memukau',
            subtitle: 'Jadikan perayaan Anda istimewa dengan kue ulang tahun spektakuler yang akan memukau semua tamu.'
          },
          {
            badge: 'Impian Pernikahan',
            title: 'Kue Pernikahan\nImpian',
            subtitle: 'Ciptakan momen ajaib dengan kue pernikahan menawan yang dirancang sesuai impian perayaan Anda.'
          },
          {
            badge: 'Cupcake Manis',
            title: 'Cupcake\nSempurna',
            subtitle: 'Hadirkan kebahagiaan dalam gigitan kecil dengan cupcake berkualitas yang sempurna untuk setiap kesempatan.'
          },
          {
            badge: 'Halal Certified',
            title: 'Halal Certified',
            subtitle: 'Kami berkomitmen menyediakan kue bersertifikat halal dengan bahan berkualitas tertinggi dan standar.',
            ctaLabel: 'Pelajari Lebih Lanjut',
            ctaLink: '/about',
            hidePrimaryCta: true
          },
          {
            badge: 'Untuk Bisnis',
            title: 'Kue Premium\nuntuk Bisnis Anda',
            subtitle: 'Kue grosir & solusi custom untuk hotel, kafe, restoran & event organizer.',
            ctaLabel: 'Untuk Bisnis',
            ctaLink: '/for-business',
            hidePrimaryCta: true
          }
        ]
      },

      // About Section
      about: {
        title: 'Mengapa Memilih TN Cakes Bali®?',
        subtitle: 'Kami memadukan seni dengan rasa untuk menciptakan kue yang cantik sekaligus lezat',
        custom: {
          title: 'Desain Custom',
          description: 'Kami mewujudkan visi unik Anda dengan desain personal untuk perayaan Anda'
        },
        quality: {
          title: 'Jaminan Kualitas',
          description: 'Hanya bahan terbaik yang digunakan untuk memastikan rasa luar biasa dan presentasi cantik'
        },
        guarantee: {
          title: 'Kepuasan Terjamin',
          description: 'Kami menjamin kepuasan Anda untuk setiap kreasi custom yang kami buat'
        },
        timely: {
          title: 'Pickup Tepat Waktu',
          description: 'Kue Anda akan siap tepat saat dibutuhkan, segar dan dipresentasikan dengan indah'
        },
        care: {
          title: 'Perhatian Personal',
          description: 'Setiap pesanan mendapat perhatian personal kami untuk memastikan kepuasan Anda'
        },
        story: {
          title: 'Kisah Kami',
          paragraph1: 'TN Cakes Bali® lahir dari passion untuk menciptakan momen indah dan berkesan melalui seni kue yang luar biasa. Berlokasi di jantung Seminyak, Bali, kami telah melayani komunitas lokal dan internasional dengan kue custom premium sejak 2019.',
          paragraph2: 'Pendiri kami, Tina, memulai perjalanan di dunia baking profesional setelah bertahun-tahun membuat kue untuk keluarga dan teman. Yang dimulai sebagai hobi dengan cepat berkembang menjadi bisnis yang berkembang pesat yang dikenal karena perhatian terhadap detail, bahan berkualitas, dan desain yang memukau.',
          paragraph3: 'Hari ini, TN Cakes Bali® bangga menjadi salah satu nama terpercaya di Bali dalam desain kue custom, telah menciptakan lebih dari 1000 kue untuk ulang tahun, pernikahan, acara korporat, dan perayaan dari semua jenis.'
        },
        mission: {
          title: 'Misi Kami',
          description: 'Menyediakan pengalaman kue custom yang tak terlupakan dengan menggabungkan kreativitas artistik, bahan premium, dan layanan pelanggan yang luar biasa untuk setiap perayaan spesial.'
        },
        vision: {
          title: 'Visi Kami',
          description: 'Menjadi penyedia kue custom terdepan di Bali yang dikenal karena inovasi, kualitas, dan kemampuan mengubah impian menjadi kreasi kuliner yang indah.'
        },
        stats: {
          experience: 'Pengalaman tahun',
          customers: 'Pelanggan bahagia',
          cakes: 'Kue dibuat',
          rating: 'Rating rata-rata'
        }
      },

      // Features Section  
      features: {
        title: 'Yang Membuat Kami Istimewa',
        subtitle: 'Temukan mengapa TN Cakes Bali® adalah pilihan sempurna untuk perayaan Anda',
        handmade: {
          title: 'Keahlian Artisan',
          description: 'Setiap kue dibuat tangan oleh artisan terampil dengan pengalaman bertahun-tahun dan passion untuk kesempurnaan'
        },
        custom: {
          title: 'Desain Custom',
          description: 'Kami mewujudkan visi unik Anda dengan desain personal untuk perayaan Anda'
        },
        premium: {
          title: 'Jaminan Kualitas',
          description: 'Hanya bahan terbaik yang digunakan untuk memastikan rasa luar biasa dan presentasi cantik'
        },
        guarantee: {
          title: 'Kepuasan Terjamin',
          description: 'Kami menjamin kepuasan Anda untuk setiap kreasi custom yang kami buat'
        },
        timely: {
          title: 'Pickup Tepat Waktu',
          description: 'Kue Anda akan siap tepat saat dibutuhkan, segar dan dipresentasikan dengan indah'
        },
        care: {
          title: 'Perhatian Personal',
          description: 'Setiap pesanan mendapat perhatian personal kami untuk memastikan kepuasan Anda'
        },
        rating: '5.0/5 Bintang',
        reviews: 'Berdasarkan 567+ Ulasan'
      },

      // Product Categories
      categories: {
        title: 'Spesialisasi Kami',
        subtitle: 'Kue custom yang dirancang untuk setiap momen spesial dalam hidup Anda',
        birthday: {
          title: 'Kue Ulang Tahun',
          description: 'Kue ulang tahun ajaib yang membuat setiap perayaan tak terlupakan dengan desain dan rasa personal'
        },
        wedding: {
          title: 'Kue Pernikahan',
          description: 'Masterpiece pernikahan elegan dengan detail menawan, bahan berkualitas, dan desain romantis'
        },
        cupcakes: {
          title: 'Cupcake',
          description: 'Cupcake artisan dengan rasa gourmet, dekorasi cantik, dan porsi individual yang sempurna'
        },
        babyshower: {
          title: 'Canapé',
          description: 'Kue bertema bayi yang menggemaskan dengan warna lembut, desain menawan, dan bahan aman ibu hamil'
        },
        celebration: {
          title: 'Kue Perayaan',
          description: 'Kue perayaan custom untuk wisuda, anniversary, dan semua momen spesial Anda'
        },
        corporate: {
          title: 'Kue Korporat',
          description: 'Kue korporat profesional dengan branding custom, presentasi elegan untuk acara bisnis'
        },
        cta: {
          title: 'Siap Memesan Kue Impian Anda?',
          description: 'Biarkan kami membantu Anda menciptakan centerpiece sempurna untuk perayaan Anda',
          portfolio: 'Lihat Galeri',
          inquiry: 'Belanja Sekarang'
        }
      },

      // Common Elements
      common: {
        readMore: 'Baca Selengkapnya',
        readLess: 'Baca Lebih Sedikit',
        loading: 'Memuat...',
        error: 'Terjadi kesalahan',
        success: 'Berhasil',
        submit: 'Kirim',
        cancel: 'Batal',
        close: 'Tutup',
        save: 'Simpan',
        edit: 'Edit',
        delete: 'Hapus',
        back: 'Kembali',
        next: 'Selanjutnya',
        previous: 'Sebelumnya',
        viewAll: 'Lihat Semua',
        learnMore: 'Pelajari Lebih Lanjut',
        note: 'Catatan'
      },

      // Forms
      forms: {
        name: 'Nama Lengkap',
        email: 'Alamat Email',
        phone: 'Nomor Telepon',
        message: 'Pesan',
        subject: 'Subjek',
        required: 'Field ini wajib diisi',
        invalidEmail: 'Masukkan alamat email yang valid',
        invalidPhone: 'Masukkan nomor telepon yang valid',
        sending: 'Mengirim...',
        sent: 'Pesan berhasil dikirim!',
        error: 'Gagal mengirim pesan. Silakan coba lagi.'
      },

      // Shop Page
      inquiry: {
        title: 'Ceritakan Tentang Kue Impian Anda',
        subtitle: 'Kami ingin mendengar ide-ide Anda! Isi formulir di bawah ini dan mari ciptakan sesuatu yang indah bersama.',
        formTitle: 'Formulir Pemesanan',
        formSubtitle: 'Ceritakan detail kue impian Anda',

        personalInfo: 'Informasi Pribadi',
        cakeDetails: 'Detail Kue',

        fullName: 'Nama Lengkap',
        email: 'Alamat Email',
        whatsapp: 'Nomor WhatsApp',
        dateNeeded: 'Tanggal Dibutuhkan',
        occasion: 'Acara',
        servings: 'Jumlah orang yang akan dilayani',
        theme: 'Deskripsi Tema/Gaya',
        message: 'Pesan Tambahan',
        specialRequests: 'Permintaan Khusus',
        uploadImages: 'Unggah screenshot inspirasi kue Anda',
        optional: 'Opsional',

        placeholders: {
          fullName: 'contoh: Jane Doe',
          email: 'contoh: jane.doe@example.com',
          whatsapp: 'contoh: 812 3456 7890',
          occasion: 'Pilih jenis acara...',
          theme: 'Jelaskan tema, warna, gaya yang Anda inginkan...',
          servings: 'contoh: 25',
          message: 'Pantangan makanan, instruksi pickup, atau permintaan lainnya...',
        },

        uploadText: 'Seret file ke sini atau klik untuk browse',
        uploadHint: 'Anda dapat mengunggah beberapa gambar (PNG, JPG hingga 5MB per file)',
        filesSelected: 'file dipilih',

        submitButton: 'Belanja Sekarang',
        submitting: 'Mengirim, harap tunggu...',
        submitHint: 'Kami akan merespons dalam 24 jam dengan penawaran detail.',
        submittingHint: 'Memproses pesanan Anda, jangan tutup halaman ini.',
        responseTime: 'Kami akan menghubungi Anda kembali dalam 24 jam.',

        successTitle: 'Pesanan Terkirim!',
        successMessage: 'Terima kasih atas pesanan Anda! Kami akan menghubungi Anda dalam 24 jam dengan penawaran detail dan proposal desain.',
        errorTitle: 'Pengiriman Gagal',
        errorMessage: 'Maaf, terjadi kesalahan saat mengirim pesanan. Silakan coba lagi atau hubungi kami langsung via WhatsApp.',

        occasions: {
          birthday: 'Pesta Ulang Tahun',
          wedding: 'Pernikahan',
          anniversary: 'Anniversary',
          babyShower: 'Baby Shower',
          corporate: 'Acara Perusahaan',
          graduation: 'Wisuda',
          other: 'Perayaan Lainnya'
        },

        howItWorks: {
          title: 'Cara Kerja',
          subtitle: 'Proses mudah dan transparan untuk mendapatkan kue custom impian Anda',
          step1: {
            title: 'Kirim Permintaan',
            description: 'Isi formulir dengan kebutuhan kue dan inspirasi Anda'
          },
          step2: {
            title: 'Dapatkan Penawaran',
            description: 'Terima penawaran detail dan proposal desain dengan cepat'
          },
          step3: {
            title: 'Konfirmasi Pesanan',
            description: 'Setujui desain dan bayar DP untuk mengamankan tanggal Anda'
          },
          step4: {
            title: 'Nikmati Kue Anda',
            description: 'Kue custom cantik Anda akan siap untuk diambil tepat waktu'
          }
        },

        stats: {
          cakes: 'Kue dibuat',
          response: 'Respon Cepat',
          rating: 'Rating Customer'
        },

        securityChallenge: 'Silakan tambahkan 2 angka dan masukkan totalnya di kotak di bawah',
        answerPlaceholder: 'Jawaban',
        securityProtected: 'Dilindungi oleh keamanan anti-bot'
      },

      // Contact Page
      contact: {
        title: 'Hubungi Kami',
        subtitle: 'Siap membahas kue impian Anda? Kami di sini untuk membantu mewujudkan perayaan yang tak terlupakan.',
        whatsappTony: 'WhatsApp Tony (English)',
        whatsappNila: 'WhatsApp Nila (Bahasa)',
        whatsappButton: 'Chat di WhatsApp',
        location: 'Kunjungi Toko Kami',
        address: 'Jl. Dewi Saraswati No.9\nSeminyak, Bali\nIndonesia',
        getDirections: 'Petunjuk Arah',
        findUs: 'Temukan Kami',
        businessHours: 'Jam Operasional',
        followUs: 'Ikuti Kami',
        bookingNote: 'Kami merekomendasikan untuk booking konsultasi terlebih dahulu untuk memastikan ketersediaan.',
        hours: {
          monday: 'Senin - Jumat',
          mondayTime: '09:00 - 18:00',
          saturday: 'Sabtu',
          saturdayTime: '09:00 - 16:00',
          sunday: 'Minggu',
          sundayTime: 'Hanya dengan Janji Temu'
        },
        defaultMessages: {
          tony: 'Hi Tony! Saya tertarik untuk memesan kue custom. Bisakah Anda memberikan informasi lebih lanjut?',
          nila: 'Hi Nila! Saya tertarik untuk memesan kue custom. Bisakah Anda memberikan informasi lebih lanjut?'
        },
        faq: {
          title: 'Pertanyaan yang Sering Diajukan',
          questions: {
            advance: {
              question: 'Berapa lama sebelumnya saya harus memesan?',
              answer: 'Kami merekomendasikan untuk memesan setidaknya 2-3 minggu sebelumnya untuk kue custom, terutama saat musim ramai.'
            },
            pickup: {
              question: 'Apakah menyediakan layanan pickup?',
              answer: 'Ya, semua kue tersedia untuk diambil di lokasi kami di Bali. Kami tidak menyediakan layanan pengiriman.'
            },
            dietary: {
              question: 'Bisakah mengakomodasi pantangan makanan?',
              answer: 'Tentu saja! Kami bisa membuat pilihan bebas gluten, vegan, dan bebas gula. Mohon sebutkan ini dalam permintaan Anda.'
            },
            cancellation: {
              question: 'Bagaimana kebijakan pembatalan?',
              answer: 'Pembatalan yang dilakukan 72 jam sebelum pickup berhak mendapat refund 50% dari DP.'
            }
          }
        },
        cta: {
          title: 'Siap Memulai Pesanan Anda?',
          subtitle: 'Jangan tunggu lagi – mari ciptakan kue sempurna Anda hari ini!',
          sendInquiry: 'Belanja Sekarang',
          whatsappUs: 'WhatsApp Kami'
        }
      },

      // Portfolio Page
      portfolio: {
        title: 'Galeri Kami',
        subtitle: 'Temukan koleksi kue custom buatan tangan yang telah membuat perayaan tak terlupakan. Setiap kreasi menceritakan kisah cinta, seni, dan cita rasa luar biasa.',
        followInstagram: 'Ikuti @tncakesbali',
        categories: {
          all: 'Semua Kue',
          wedding: 'Kue Pernikahan',
          birthday: 'Kue Ulang Tahun',
          cupcakes: 'Cupcake',
          corporate: 'Acara Perusahaan',
          baby: 'Canapé',
          celebration: 'Kue Perayaan'
        },
        viewDetails: 'Lihat Detail',
        learnMore: 'Pelajari Lebih Lanjut',
        modal: {
          aboutCreation: 'Tentang Kreasi Ini',
          keyFeatures: 'Fitur Utama',
          perfectFor: 'Sempurna Untuk',
          preparationTime: 'Waktu Persiapan',
          interestedTitle: 'Tertarik dengan Desain Ini?',
          startOrder: 'Mulai Pesanan',
          whatsappUs: 'WhatsApp Kami'
        },
        cta: {
          title: 'Siap Menciptakan Kue Impian Anda?',
          subtitle: 'Mari diskusikan visi Anda dan wujudkan dengan layanan kue custom berkualitas kami.',
          startOrder: 'Mulai Pesanan',
          whatsappUs: 'WhatsApp Kami'
        }
      },

      // Footer
      footer: {
        description: 'Menciptakan kue custom yang indah dan lezat untuk momen spesial Anda di Bali.',
        quickLinks: 'Tautan Cepat',
        services: 'Layanan',
        contact: 'Informasi Kontak',
        address: 'Alamat',
        phone: 'Telepon',
        email: 'Email',
        hours: 'Jam Buka',
        social: 'Ikuti Kami',
        copyright: '© 2025 TN Cakes Bali®. Hak cipta dilindungi.',
        newsletter: {
          title: 'Tetap Update',
          subtitle: 'Berlangganan untuk menerima update tentang desain kue baru dan penawaran spesial',
          placeholder: 'Masukkan email Anda',
          subscribe: 'Berlangganan',
          success: 'Terima kasih sudah berlangganan!',
          error: 'Mohon masukkan alamat email yang valid'
        }
      },

      // Global CTA Section
      globalCTA: {
        title: 'Siap Membuat Momen Anda Istimewa?',
        subtitle: 'Bergabunglah dengan ratusan pelanggan puas yang telah mempercayai TN Cakes Bali® untuk momen spesial mereka.',
        orderNow: 'Pesan Sekarang',
        viewGallery: 'Lihat Galeri',
        contactUs: 'Hubungi Kami'
      },

      // Home Page CTA Section
      homeCTA: {
        title: 'Siap Menciptakan Kue Impian Anda?',
        subtitle: 'Mari wujudkan visi Anda menjadi kenyataan dengan layanan kue custom premium kami. Hubungi kami hari ini untuk mulai merencanakan perayaan sempurna Anda.',
        shopNow: 'Belanja Sekarang',
        viewPortfolio: 'Lihat Portofolio'
      },

      // For Business Page
      forBusiness: {
        hero: {
          title: 'Solusi Kue Custom untuk Bisnis Anda',
          subtitle: 'Kami mendukung hotel, kafe, restoran, dan event organizer dengan kue premium untuk kebutuhan tamu dan acara spesial.'
        },
        value: {
          quality: {
            title: 'Kualitas Premium',
            description: 'Bahan premium, halal, dibuat segar setiap hari.'
          },
          branding: {
            title: 'Branding Custom',
            description: 'Kue dengan logo perusahaan Anda atau tema acara khusus.'
          },
          delivery: {
            title: 'Pengantaran Andal',
            description: 'Tepat waktu untuk kebutuhan event dan tamu Anda.'
          },
          orders: {
            title: 'Pesanan Fleksibel',
            description: 'Tersedia pemesanan grosir dan berulang.'
          }
        },
        solutions: {
          title: 'Paket & Solusi',
          hotels: {
            title: 'Hotel & Resort',
            description: 'Welcome cake, paket honeymoon, kejutan kamar.'
          },
          corporate: {
            title: 'Acara Korporat',
            description: 'Meeting, seminar, workshop, peluncuran produk.'
          },
          cafe: {
            title: 'Kafe & Restoran',
            description: 'Kolaborasi dessert, menu musiman.'
          },
          retail: {
            title: 'Ritel & Grosir',
            description: 'Cupcake dalam jumlah besar, hampers untuk reseller.'
          }
        },
        partners: {
          title: 'Siapa yang mempercayai kami',
          testimonial: 'Bekerja sama dengan TN Cakes meningkatkan pengalaman tamu kami. Kualitas dan layanan mereka tak tertandingi.',
          testimonialFrom: 'Manajer, The Grand Hotel'
        },
        cta: {
          title: 'Ayo Berkolaborasi',
          button: 'Kirim Permintaan',
          altCta: 'Atau hubungi kami langsung via WhatsApp Business.'
        },
        form: {
          businessName: 'Nama Bisnis',
          businessType: 'Jenis Bisnis',
          needs: 'Deskripsikan Kebutuhan Anda',
          orderEstimate: 'Estimasi Jumlah Pesanan',
          contact: 'Kontak (Nama & Telepon)'
        }
      },

      // Charity Page
      charity: {
        title: "Berbagi Manis, Menebar Kebaikan",
        subtitle: "Setiap potongan kue bukan hanya untuk merayakan, tapi juga berbagi kebahagiaan dengan mereka yang membutuhkan.",
        about: {
          title: "Tentang Program Amal Kami",
          description: "Kami percaya bisnis bukan hanya tentang profit, tapi juga tentang memberi dampak positif bagi masyarakat. Sebagian profit atau pesanan tertentu dialokasikan untuk program sosial yang fokus pada anak-anak, panti asuhan, dan komunitas lokal di Bali."
        },
        support: {
          title: "Bentuk Dukungan Anda",
          cakeDonation: {
            title: "Donasi Kue",
            description: "Kami mengirimkan kue untuk anak-anak panti asuhan, sekolah, atau komunitas untuk berbagi momen kebahagiaan."
          },
          events: {
            title: "Acara Amal",
            description: "Kami berkolaborasi dengan yayasan/NGO untuk mengadakan acara sosial dan penggalangan dana."
          },
          participation: {
            title: "Partisipasi Pelanggan",
            description: "Setiap pembelian 1 cake custom, Rp50.000 akan didonasikan untuk program amal kami."
          }
        },
        gallery: {
          title: "Dokumentasi & Cerita",
          subtitle: "Galeri foto dan video dari kegiatan amal yang telah kami selenggarakan."
        },
        cta: {
          title: "Bergabunglah Menebar Kebaikan",
          subtitle: "Berkolaborasi dengan kami untuk memberikan lebih banyak senyuman. Apakah Anda ingin berdonasi dana, barang, atau bekerja sama untuk sebuah acara, kami terbuka untuk semua bentuk dukungan.",
          button: "Berkolaborasi dengan Kami"
        },
        form: {
          title: "Formulir Kolaborasi & Donasi",
          name: "Nama Lengkap atau Organisasi",
          contact: "Kontak (Telepon/Email)",
          contributionType: "Jenis Kontribusi",
          message: "Pesan Anda",
          submit: "Kirim Penawaran",
          contributionOptions: {
            donation: "Donasi Finansial",
            goods: "Donasi Barang",
            event: "Kolaborasi Acara"
          }
        },
        stats: {
          cakesDonated: "Kue Didonasikan",
          eventsSupported: "Acara Amal Didukung",
          smilesDelivered: "Senyuman Terkirim"
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,

    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
